import React, { FunctionComponent, useRef } from 'react';
import {
  Avatar,
  Button,
  Divider,
  Empty,
  Flex,
  Image,
  Select,
  Typography,
} from 'antd';
import { CrownOutlined, PlusOutlined } from '@ant-design/icons';
import { useStore } from '../../helpers/use-store';
import { getEnv } from '../../helpers/mobx-easy-wrapper';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { observer } from 'mobx-react-lite';
import SocialIcon from '../../components/social-provider-icon/social-provider-icon';
import {
  SocialAccountsModal,
  SocialAccountsModalRef,
} from './social-accounts-modal';
import { SocialAccount, SocialProvider } from '@monorepo/types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface SocialAccountSelectProps {
  value?: string;
  onChange?: (value: any) => void;
  disabled?: boolean;
  loading?: boolean;
  socialTypes?: SocialProvider[];
  isMultiple?: boolean;
}

const SocialAccountSelect: FunctionComponent<SocialAccountSelectProps> = ({
  value,
  onChange,
  loading = false,
  disabled,
  isMultiple = false,
  socialTypes = [SocialProvider.TikTok, SocialProvider.YouTube],
}) => {
  const { t } = useTranslation();
  const {
    envConfig: { youtube_client_id },
  } = getEnv();
  const navigate = useNavigate();
  const {
    dataStore: {
      socialAccountStore,
      userStore: { currentUser, isSubscriptionActive },
    },
  } = useStore();
  const modalRef = useRef<SocialAccountsModalRef>(null);

  const handleAddAccount = () => {
    modalRef.current?.open();
  };

  const handleAccountAdded = () => {
    socialAccountStore.fetchAll();
    modalRef.current?.close();
  };

  const socialAccounts = socialAccountStore.items.filter((account) => {
    return socialTypes.includes(account.provider);
  });

  const displaySocialAccountsOnly = socialAccountStore.items.filter(
    (account) => {
      return (
        account._id === value ||
        (Array.isArray(value) && value.includes(account._id))
      );
    }
  );

  const renderSocialAccountRow = (account: SocialAccount) => {
    const title = (
      <Flex gap={20} align={'center'}>
        <SocialIcon provider={account.provider} />
        <Flex align={'center'} gap={5}>
          <Avatar
            size={'small'}
            src={
              <Image
                crossOrigin={'anonymous'}
                referrerPolicy={'no-referrer'}
                src={account.avatarUrl}
                preview={false}
              />
            }
          />
          <span>{account.title}</span>
        </Flex>
      </Flex>
    );

    let isDisabled = account.tokenInvalid;

    if (account.provider === SocialProvider.TikTok && !isDisabled) {
      isDisabled = currentUser.applicationConfig?.disableTiktok || false;
    }

    if (account.provider === SocialProvider.YouTube && !isDisabled) {
      isDisabled = currentUser.applicationConfig?.disableYoutube || false;
    }

    return (
      <Select.Option
        disabled={isDisabled}
        key={account._id}
        value={account._id}
        label={title}
      >
        <Flex style={{ flexDirection: 'column' }}>
          <span>{title}</span>
          {account.tokenInvalid ? (
            <Typography.Text type={'secondary'} style={{ fontSize: '12px' }}>
              {t('socialAccounts.account.reconnect')}
            </Typography.Text>
          ) : null}
        </Flex>
      </Select.Option>
    );
  };

  return (
    <>
      <Select
        loading={loading}
        disabled={disabled}
        style={{ width: '100%' }}
        value={value}
        mode={isMultiple ? 'multiple' : undefined}
        onChange={onChange}
        notFoundContent={
          <Empty description={t('socialAccounts.noAccountsDropdown')} />
        }
        placeholder={t('socialAccounts.selectPlaceholder')}
        optionLabelProp="label"
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '8px 0' }} />

            {!isSubscriptionActive ? (
              <div style={{ padding: '8px 12px' }}>
                <Typography.Text
                  type="secondary"
                  style={{
                    fontSize: '13px',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  {t('socialAccounts.upgradeUnlock')}
                </Typography.Text>
                <Button
                  type="primary"
                  icon={<CrownOutlined />}
                  block
                  onClick={() => navigate('/credits')}
                  style={{ marginBottom: '4px' }}
                >
                  {t('subscribeCta.button')}
                </Button>
              </div>
            ) : (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                block
                onClick={handleAddAccount}
              >
                {t('socialAccounts.connectSocialAccount')}
              </Button>
            )}
          </>
        )}
      >
        {displaySocialAccountsOnly.map((account) =>
          renderSocialAccountRow(account)
        )}
        {socialAccounts.map((account) => renderSocialAccountRow(account))}
      </Select>

      <GoogleOAuthProvider clientId={youtube_client_id}>
        <SocialAccountsModal
          ref={modalRef}
          onAccountAdded={handleAccountAdded}
        />
      </GoogleOAuthProvider>
    </>
  );
};

export default observer(SocialAccountSelect);
